import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_CATS = ["colazione", "caffetteria", "brunch", "aperitivo", "cucina", "dolci", "vini", "cocktail"];
const emptyIng = { product_id: "", product_name: "", quantity: 0, unit: "" };
const emptyForm = { name: "", category: "cucina", servings: 1, prep_time: 5, description: "", ingredients: [] };

export default function Recipes() {
  const { lists } = useLists();
  const { can } = useAuth();
  // FASE 7.4 STEP F.1 — recipes mutations require recipes.write.
  const canWrite = can("recipes.write");
  const CATS = lists?.menu_categories?.length ? lists.menu_categories : DEFAULT_CATS;
  const [list, setList] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [q, setQ] = useState("");

  const load = async () => {
    const [r, i] = await Promise.all([api.get("/recipes"), api.get("/inventory")]);
    setList(r.data); setInventory(i.data);
  };
  useEffect(() => { load(); }, []);

  // live cost preview inside the dialog
  const livePreview = useMemo(() => {
    let total = 0;
    for (const ing of form.ingredients) {
      const p = inventory.find((x) => x.id === ing.product_id);
      if (!p) continue;
      total += Number(ing.quantity || 0) * Number(p.unit_cost || 0);
    }
    const servings = Math.max(Number(form.servings) || 1, 1);
    return { total, perServing: total / servings };
  }, [form, inventory]);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, category: CATS[0] || "cucina" }); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "", category: r.category || CATS[0],
      servings: r.servings ?? 1, prep_time: r.prep_time ?? 5,
      description: r.description || "",
      ingredients: (r.ingredients || []).map((i) => ({ ...i })),
    });
    setOpen(true);
  };

  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { ...emptyIng }] }));
  const setIng = (idx, patch) => setForm((f) => {
    const arr = f.ingredients.slice();
    arr[idx] = { ...arr[idx], ...patch };
    return { ...f, ingredients: arr };
  });
  const rmIng = (idx) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      servings: Number(form.servings),
      prep_time: Number(form.prep_time),
      ingredients: form.ingredients
        .filter((i) => i.product_id && Number(i.quantity) > 0)
        .map((i) => ({ ...i, quantity: Number(i.quantity) })),
    };
    try {
      if (editing) await api.put(`/recipes/${editing.id}`, payload);
      else await api.post("/recipes", payload);
      toast.success(editing ? "Ricetta aggiornata" : "Ricetta creata");
      setOpen(false);
      load();
    } catch { toast.error("Errore salvataggio"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Eliminare "${r.name}"?`)) return;
    try { await api.delete(`/recipes/${r.id}`); toast.success("Eliminata"); load(); }
    catch { toast.error("Errore"); }
  };

  const filtered = useMemo(() =>
    list.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase())),
    [list, q]
  );

  return (
    <div data-testid="recipes-page">
      <PageHeader eyebrow="Cucina & Bar" title="Ricette"
        subtitle="Modifica dosi, porzioni e ingredienti. Il costo porzione si ricalcola in tempo reale dai prezzi del magazzino."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            {canWrite && (
            <DialogTrigger asChild>
              <Button data-testid="new-recipe-btn" onClick={openNew} className="bg-neutral-900 text-[#FDFBF7]">
                <Plus size={14} strokeWidth={1.5} /> Nuova ricetta
              </Button>
            </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif">{editing ? "Modifica ricetta" : "Nuova ricetta"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Nome</Label><Input data-testid="rec-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Categoria</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="rec-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        {form.category && !CATS.includes(form.category) && <SelectItem value={form.category}>{form.category}</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Porzioni</Label><Input data-testid="rec-servings" type="number" min="1" value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} /></div>
                  <div><Label>Tempo prep. (min)</Label><Input type="number" min="0" value={form.prep_time} onChange={(e) => setForm({ ...form, prep_time: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                </div>

                <div className="border-t border-[#E2D9C8] pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="label-eyebrow">Ingredienti</div>
                    <Button type="button" size="sm" variant="outline" onClick={addIng} data-testid="rec-add-ing">
                      <Plus size={12} strokeWidth={1.5} /> Aggiungi ingrediente
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {form.ingredients.map((ing, idx) => {
                      const prod = inventory.find((p) => p.id === ing.product_id);
                      const lineCost = prod ? Number(ing.quantity || 0) * Number(prod.unit_cost || 0) : 0;
                      return (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`rec-ing-${idx}`}>
                          <div className="col-span-6">
                            <Select value={ing.product_id || "none"} onValueChange={(v) => {
                              const p = inventory.find((x) => x.id === v);
                              setIng(idx, { product_id: v === "none" ? "" : v, product_name: p?.name || "", unit: p?.unit || ing.unit || "" });
                            }}>
                              <SelectTrigger data-testid={`rec-ing-prod-${idx}`}><SelectValue placeholder="Prodotto…" /></SelectTrigger>
                              <SelectContent className="max-h-60">
                                <SelectItem value="none">—</SelectItem>
                                {inventory.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit})</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-2">
                            <Input data-testid={`rec-ing-qty-${idx}`} type="number" step="0.001" min="0"
                              placeholder="qtà" value={ing.quantity}
                              onChange={(e) => setIng(idx, { quantity: e.target.value })} />
                          </div>
                          <div className="col-span-2">
                            <Input value={ing.unit} placeholder="unità" onChange={(e) => setIng(idx, { unit: e.target.value })} />
                          </div>
                          <div className="col-span-1 text-right text-xs text-neutral-500">{fmtEUR(lineCost)}</div>
                          <div className="col-span-1 text-right">
                            <button type="button" onClick={() => rmIng(idx)} className="text-neutral-400 hover:text-red-700"><X size={14} strokeWidth={1.5} /></button>
                          </div>
                        </div>
                      );
                    })}
                    {form.ingredients.length === 0 && (
                      <div className="text-xs text-neutral-500 border border-dashed border-[#E2D9C8] rounded p-4 text-center">Nessun ingrediente. Aggiungine uno per calcolare il costo.</div>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#E2D9C8] pt-4 flex items-center justify-between bg-[#F3EDE1] -mx-6 px-6 py-3 rounded">
                  <div>
                    <div className="label-eyebrow">Costo totale ricetta</div>
                    <div className="font-serif text-2xl mt-1">{fmtEUR(livePreview.total)}</div>
                  </div>
                  <div className="text-right">
                    <div className="label-eyebrow">Costo per porzione</div>
                    <div className="font-serif text-2xl mt-1" data-testid="rec-live-per-serving">{fmtEUR(livePreview.perServing)}</div>
                  </div>
                </div>

                <DialogFooter>
                  <Button data-testid="rec-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">{editing ? "Salva modifiche" : "Crea ricetta"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        } />

      <div className="mb-4">
        <Input data-testid="rec-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ricetta…" className="max-w-xs" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((r) => (
          <div key={r.id} className="border border-[#E2D9C8] rounded-lg p-5 bg-white" data-testid={`rec-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">{r.category}</div>
                <div className="font-serif text-2xl mt-1">{r.name}</div>
              </div>
              {canWrite && (
              <div className="flex gap-1">
                <button onClick={() => openEdit(r)} data-testid={`edit-rec-${r.id}`} className="text-neutral-500 hover:text-neutral-900"><Pencil size={14} strokeWidth={1.5} /></button>
                <button onClick={() => del(r)} data-testid={`del-rec-${r.id}`} className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
              </div>
              )}
            </div>
            <div className="mt-4 space-y-1 text-sm">
              {r.ingredients?.map((ing, i) => (
                <div key={i} className="flex justify-between text-neutral-600">
                  <span>{ing.product_name}</span>
                  <span>{ing.quantity} {ing.unit}</span>
                </div>
              ))}
              {(!r.ingredients || r.ingredients.length === 0) && (
                <div className="text-xs text-neutral-500 italic">Nessun ingrediente</div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-[#E2D9C8] flex justify-between">
              <div>
                <div className="label-eyebrow">Costo porzione</div>
                <div className="font-serif text-2xl">{fmtEUR(r.cost_per_serving)}</div>
              </div>
              <div className="text-right">
                <div className="label-eyebrow">Tempo</div>
                <div className="font-serif text-2xl">{r.prep_time || 0}'</div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full border border-dashed border-[#E2D9C8] rounded-lg p-10 text-center text-neutral-500">
            Nessuna ricetta. Crea la prima con il pulsante in alto.
          </div>
        )}
      </div>
    </div>
  );
}
